import React, { useEffect, useState } from 'react';
import { Check, Loader2, Send, X } from 'lucide-react';
import { onboardingApi } from '../../services/api';
import { revisionFrom, revisionProblems, resubmitPayload } from '../../utils/onboarding';

const CORRIDORS = ['FSPIOP', 'ISO20022', 'ISO8583'];

function Input({ label, value, onChange, placeholder, hint, status }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
      {(hint || status) && <span className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">{hint}{status}</span>}
    </label>
  );
}

const Availability = ({ checking, ok }) => {
  if (checking) return <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> checking…</span>;
  if (ok === undefined) return null;
  return ok
    ? <span className="inline-flex items-center gap-1 text-green-600"><Check className="w-3.5 h-3.5" /> available</span>
    : <span className="inline-flex items-center gap-1 text-red-500"><X className="w-3.5 h-3.5" /> taken</span>;
};

/**
 * The applicant's revision of an application AfricaNenda sent back with a
 * request for changes. Sending it returns the application to the review queue.
 */
export default function ResubmitForm({ app, statusToken, onDone }) {
  const [f, setF] = useState(() => revisionFrom(app));
  const [avail, setAvail] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleCorridor = (c) => set('corridors', f.corridors.includes(c) ? f.corridors.filter((x) => x !== c) : [...f.corridors, c]);

  // Live availability of the identifiers, as in the application wizard.
  useEffect(() => {
    if (!f.proposedTenantId) { setAvail(null); return undefined; }
    setChecking(true);
    const t = setTimeout(() => {
      onboardingApi.availability(f.proposedTenantId, f.proposedFspId)
        .then(setAvail).catch(() => setAvail(null)).finally(() => setChecking(false));
    }, 400);
    return () => clearTimeout(t);
  }, [f.proposedTenantId, f.proposedFspId]);

  const problems = revisionProblems(f, avail);
  const current = avail && avail.tenantId === f.proposedTenantId;

  const send = async () => {
    setBusy(true); setError(null);
    try { onDone(await onboardingApi.resubmit(statusToken, resubmitPayload(app, f))); }
    catch (e) { setError(e.response?.data?.error || e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-5 border border-gray-200 rounded-lg p-4 space-y-3 text-left">
      <div>
        <p className="text-sm font-semibold text-gray-900">Revise your application</p>
        <p className="text-xs text-gray-500 mt-0.5">Make the changes asked for above, then send it back. AfricaNenda will review it again.</p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}

      <Input label="Organisation legal name" value={f.orgLegalName} onChange={(v) => set('orgLegalName', v)} placeholder="Acme Payments Ltd" />
      <Input label="Display name" value={f.displayName} onChange={(v) => set('displayName', v)} placeholder="Acme Payments" />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Country" value={f.country} onChange={(v) => set('country', v.toUpperCase().slice(0, 2))} placeholder="RW" hint="2-letter code" />
        <Input label="Website" value={f.website} onChange={(v) => set('website', v)} placeholder="https://acme.example" />
      </div>
      <Input label="Proposed tenant id" value={f.proposedTenantId} onChange={(v) => set('proposedTenantId', v.toLowerCase())}
        placeholder="acme-rw" hint="Lowercase letters, numbers and hyphens."
        status={<Availability checking={checking} ok={current && !checking ? avail.tenantIdAvailable : undefined} />} />
      <Input label="Proposed FSP id" value={f.proposedFspId} onChange={(v) => set('proposedFspId', v)} placeholder="AcmeFSP"
        status={<Availability checking={false} ok={current && !checking && f.proposedFspId ? avail.fspIdAvailable : undefined} />} />
      <div>
        <span className="text-xs font-medium text-gray-700">Corridors</span>
        <div className="flex flex-wrap gap-4 mt-1.5">
          {CORRIDORS.map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={f.corridors.includes(c)} onChange={() => toggleCorridor(c)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
              {c}
            </label>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="text-xs font-medium text-gray-700">Intended use <span className="text-gray-400 font-normal">(optional)</span></span>
        <textarea value={f.intendedUse} onChange={(e) => set('intendedUse', e.target.value)} rows={2}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary-500" />
      </label>
      <Input label="Primary contact name" value={f.contactName} onChange={(v) => set('contactName', v)} placeholder="Jane Doe" />
      <div>
        <p className="text-xs font-medium text-gray-700">Contact email</p>
        <p className="text-sm text-gray-900 mt-1">{app.contactEmail}</p>
        <p className="text-xs text-gray-400 mt-0.5">Verified, so it can't be changed here. To use another address, email AfricaNenda.</p>
      </div>

      <div className="pt-1">
        <button onClick={send} disabled={busy || problems.length > 0}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send for review
        </button>
        {problems.length > 0 && <p className="text-xs text-gray-500 mt-2 text-center">{problems[0]}</p>}
      </div>
    </div>
  );
}
